// src/pages/Reports/CurrentLiability/CurrentLiabilityBalanceReport.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  Select,
  AutoComplete,
  Input,
  Table,
  message,
  Grid,
  Flex,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";

import ReportDom from "../../../components/common/ReportDom";
import {
  currentDateStartTime,
  currentDateEndTime,
  dateFormat,
} from "../../../lib/functions";
import {
  getCurrentLiabilityBalanceApi,
} from "../../../apiservice/reports";
import {
  getAccountBySeachApi,
} from "../../../apiservice/setting";
import { useLocation, useSearchParams } from "react-router-dom";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { exportCurrentLiabilityCsv } from "./exportCurrentLiabilityCsv";
import "../../../components/common/bt.css";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const CurrentLiabilityBalanceReport = () => {
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    filterType: "all", // all | account | date
    account: null,
    fromDate: currentDateStartTime(dayjs()),
    toDate: currentDateEndTime(dayjs()),
  });

    // Drill-down from Balance Sheet: /reports/current-liablity-report/<toDate>?fromDate=<fromDate>
    // handleFilterChange only schedules a state update, so calling fetchReport()
    // right after it would still read the pre-update (stale) filters — build the
    // fetched filters directly instead and pass them to fetchReport explicitly.
    useEffect(() => {
      const path = pathname.split("/")[3];
      if (!path) return;
      const fromDate = searchParams.get("fromDate") || null;
      const nextFilters = { filterType: "date", fromDate, toDate: path };
      setFilters((prev) => ({ ...prev, ...nextFilters }));
      fetchReport(nextFilters);
    }, []);

  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accLoading, setAccLoading] = useState(false);
  const [balanceTotal, setBalanceTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // override lets the Balance Sheet drill-down (mount effect) fetch with
  // freshly parsed URL values instead of waiting a render for `filters` state
  // to update — see the mount effect above.
  const fetchReport = async (override) => {
    const f = override ?? filters;
    if (f.filterType === "account" && !f.account) {
      message.warning("Please select an account");
      return;
    }

    setLoading(true);
    try {
      let payload = {};

      if (f.filterType === "all") {
        payload = { accId: null };
      } else if (f.filterType === "account") {
        payload = { accId: f.account?.acc_id ?? null };
      } else if (f.filterType === "date") {
        payload = { fromDate: f.fromDate, toDate: f.toDate };
      }

      const res = await getCurrentLiabilityBalanceApi(payload);

      // Backend names these by cash-flow direction (received_total = journal
      // credits = money borrowed, increases the liability; pay_total = journal
      // debits = payments, decreases it) — rename to the accounting Debit/
      // Credit sense the columns below use, so a debit shows as a debit.
      const mapped = (res.data.liabilitys || []).map((item) => ({
        ...item,
        opening_balance: item.opening_balance ?? item.curr_opening_balance ?? 0,
        debit_total: item.pay_total ?? 0,
        credit_total: item.received_total ?? 0,
        balance: item.balance ?? 0,
      }));

      setData(mapped);
      setBalanceTotal(res.data.total_balance || 0);
    } catch (err) {
      console.error(err);
      message.error("Error fetching Current Liability Balance Report");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async (query) => {
    setAccLoading(true);
    try {
      const res = await getAccountBySeachApi({
        query: query?.trim() || "",
        multiType: ["current_liability"],
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    } finally {
      setAccLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Current Liability Balance Report",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };
  const handleDownloadCsv = () => {                        // 👈 added
    exportCurrentLiabilityCsv({
      data,
      balanceTotal,
      title:    "Current Liability Balance Report",
      filename: "current_liability_balance",
    });
  };
  const columns = [
    { title: "No.", render: (_, __, i) => i + 1 },
    { title: "Account Name", dataIndex: "acc_name" },
    {
      title: "Opening Balance",
      dataIndex: "opening_balance",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Debit Amount",
      dataIndex: "debit_total",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Credit Amount",
      dataIndex: "credit_total",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle">
              Current Liability Balance Report
            </Title>
          </div>
        }
       extra={
          data.length > 0 ? (                              // 👈 guard added + Flex wrapper
            <Flex gap={8}>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                {!screens.xs && "CSV"}
              </Button>
              <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                {!screens.xs && "Print"}
              </Button>
            </Flex>
          ) : (
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          )
        }
      />

      {/* Filters */}
      <Card className="shadow-md rounded-xl mb-4">
  <Row gutter={[16, 16]}>
    {/* Filter Type */}
    <Col xs={24} sm={12} md={6} lg={6}>
      <label>Filter Type</label>
      <Select
        value={filters.filterType}
        onChange={(val) => handleFilterChange("filterType", val)}
        style={{ width: "100%" }}
        options={[
          { value: "all", label: "All Current Liability Accounts" },
          { value: "account", label: "Account Wise" },
          { value: "date", label: "Date Wise" },
        ]}
      />
    </Col>

    {/* Account Wise */}
    {filters.filterType === "account" && (
      <Col xs={24} sm={12} md={6} lg={6}>
        <label>Select Account</label>
        <AutoComplete
          style={{ width: "100%" }}
          options={accounts.map((a) => ({
            label: `${a.acc_name} - ${a.acc_code || ""}`,
            value: `${a.acc_name} - ${a.acc_code || ""}`,
            account: a,
          }))}
          onSelect={(_, option) => {
            handleFilterChange("account", option.account);
          }}
          onSearch={fetchAccounts}
          placeholder="Search Account"
          loading={accLoading}
          allowClear
        >
          <Input />
        </AutoComplete>
      </Col>
    )}

    {/* Date Wise */}
    {filters.filterType === "date" && (
      <>
      <CommonDateRangeFilter
        filters={filters}
        onChange={(fromDate, toDate) => {
        handleFilterChange("fromDate", fromDate);
        handleFilterChange("toDate", toDate);
        }}
      />
        {/* <Col xs={24} sm={12} md={4} lg={4}>
          <label>From Date</label>
          <DatePicker
            value={dayjs(filters.fromDate)}
            onChange={(d) =>
              handleFilterChange("fromDate", currentDateStartTime(dayjs(d)))
            }
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={12} md={4} lg={4}>
          <label>To Date</label>
          <DatePicker
            value={dayjs(filters.toDate)}
            onChange={(d) =>
              handleFilterChange("toDate", currentDateEndTime(dayjs(d)))
            }
            style={{ width: "100%" }}
          />
        </Col> */}
      </>
    )}

    {/* Report Button */}
    <Col xs={24} sm={12} md={4} lg={4} style={{ display: "flex", alignItems: "flex-end", height: 58 }}>
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={() => fetchReport()}
        loading={loading}
        style={{height: 35.19}}
        block
      >
        Report
      </Button>
    </Col>
  </Row>
</Card>

      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
          dataSource={data}
          rowKey={(_, i) => i}
          columns={columns}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          bordered
          scroll={{ x: "max-content" }}
          size="small"
          summary={() =>
            data.length > 0 && (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={2} align="right">
                  Grand Total
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(_.sumBy(data, (r) => Number(r.opening_balance || 0)).toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(_.sumBy(data, (r) => Number(r.debit_total || 0)).toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(_.sumBy(data, (r) => Number(r.credit_total || 0)).toFixed(2))}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(balanceTotal.toFixed(2))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }
        />
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Current Liability Balance Report"
          mode="report-page"
          filters={filters}
          renderTable={() => (
            <table className="ledger-print-table">
              <colgroup>
                <col style={{ width: "6%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <tbody>
                <tr className="ledger-print-heading">
                  <td>No.</td>
                  <td>Account Name</td>
                  <td style={{ textAlign: "right" }}>Opening Balance</td>
                  <td style={{ textAlign: "right" }}>Debit Amount</td>
                  <td style={{ textAlign: "right" }}>Credit Amount</td>
                  <td style={{ textAlign: "right" }}>Balance</td>
                </tr>
                {data.map((row, index) => (
                  <tr key={row.acc_id || index}>
                    <td>{index + 1}</td>
                    <td>{row.acc_name}</td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.opening_balance || 0).toFixed(2))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.debit_total || 0).toFixed(2))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.credit_total || 0).toFixed(2))}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {format(Number(row.balance || 0).toFixed(2))}
                    </td>
                  </tr>
                ))}
                <tr className="ledger-total-row">
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(
                      _.sumBy(data, (r) => Number(r.opening_balance || 0)).toFixed(2)
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(
                      _.sumBy(data, (r) => Number(r.debit_total || 0)).toFixed(2)
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(
                      _.sumBy(data, (r) => Number(r.credit_total || 0)).toFixed(2)
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {format(Number(balanceTotal || 0).toFixed(2))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        />
      </div>
    </div>
  );
};

export default CurrentLiabilityBalanceReport;
